import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { usePermissions } from '@/contexts/PermissionsContext';
import api from '@/lib/axios';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { 
  Upload, 
  AlertTriangle, 
  CheckCircle, 
  XCircle, 
  Database, 
  Users, 
  BookOpen, 
  FileText,
  RefreshCw,
  AlertOctagon,
  Info,
  Shield,
  AlertCircle
} from 'lucide-react';
import { toast } from 'sonner';
import UrlFilterChip from '@/components/UrlFilterChip';
import Layout from '@/components/layout/Layout';

export default function MsvImport() {
  const { user } = useAuth();
  const { isSuperAdmin } = usePermissions();
  const [status, setStatus] = useState(null);
  const [preview, setPreview] = useState(null);
  const [importResult, setImportResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [importing, setImporting] = useState(false);
  const [uploadFile, setUploadFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  const uploadSourceDb = async () => {
    if (!uploadFile) return;
    setUploading(true);
    setUploadProgress(0);
    const CHUNK_SIZE = 5 * 1024 * 1024;
    try {
      const startRes = await api.post('/msv-import/upload/start');
      const uploadId = startRes.data.upload_id;
      const totalChunks = Math.ceil(uploadFile.size / CHUNK_SIZE);
      for (let i = 0; i < totalChunks; i++) {
        const blob = uploadFile.slice(i * CHUNK_SIZE, (i + 1) * CHUNK_SIZE);
        const fd = new FormData();
        fd.append('chunk', blob, 'chunk');
        await api.post(`/msv-import/upload/chunk?upload_id=${uploadId}&index=${i}`, fd, {
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        setUploadProgress(Math.min(99, Math.round(((i + 1) / totalChunks) * 100)));
      }
      const fin = await api.post(`/msv-import/upload/finish?upload_id=${uploadId}`);
      setUploadProgress(100);
      toast.success(fin.data.message);
      setUploadFile(null);
      setPreview(null);
      setImportResult(null);
      await loadStatus();
    } catch (error) {
      toast.error('Upload fehlgeschlagen: ' + (error.response?.data?.detail || error.message));
    } finally {
      setUploading(false);
    }
  };

  const canAccess = isSuperAdmin || user?.is_super_admin || user?.roles?.some(r => ['admin', 'secretary', 'sekretariat', 'verwaltung', 'super_admin'].includes(r.name));

  // Honor `?tab=conflicts` deep-link from dashboard: scroll to conflicts
  // section once the import result loads.
  const [searchParams] = useSearchParams();
  useEffect(() => {
    if (searchParams.get('tab') === 'conflicts' && importResult?.conflicts?.length) {
      const el = document.querySelector('[data-testid="conflicts-table"]');
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [searchParams, importResult]);

  useEffect(() => {
    if (canAccess) {
      loadStatus();
    }
  }, [canAccess]);

  const loadStatus = async () => {
    try {
      const response = await api.get('/msv-import/status');
      setStatus(response.data);
    } catch (error) {
      console.error('Failed to load status:', error);
    }
  };

  const runPreview = async () => {
    setLoading(true);
    setPreview(null);
    setImportResult(null);
    try {
      const response = await api.get('/msv-import/preview');
      setPreview(response.data);
      if (!response.data.can_proceed) {
        toast.error(`${response.data.blocking_errors.length} Blocking Error(s) gefunden`);
      } else if (response.data.conflicts_detected?.length > 0) {
        toast.warning(`${response.data.conflicts_detected.length} Duplikate erkannt - werden als Konflikte importiert`);
      } else {
        toast.success('Vorschau erfolgreich - Import möglich');
      }
    } catch (error) {
      toast.error('Fehler bei der Vorschau: ' + (error.response?.data?.detail || error.message));
    } finally {
      setLoading(false);
    }
  };

  const runImport = async (dryRun = false) => {
    setImporting(true);
    try {
      const response = await api.post(`/msv-import/run?dry_run=${dryRun}`);
      setImportResult(response.data);
      
      if (response.data.success) {
        if (response.data.conflicts?.length > 0) {
          toast.warning(`Import abgeschlossen mit ${response.data.conflicts.length} Konflikten`);
        } else {
          toast.success(dryRun ? 'Dry-Run erfolgreich' : 'Import erfolgreich abgeschlossen');
        }
      } else {
        toast.error('Import abgebrochen: Blocking Errors');
      }
      
      if (!dryRun && response.data.success) {
        setPreview(null);
      }
    } catch (error) {
      toast.error('Fehler beim Import: ' + (error.response?.data?.detail || error.message));
    } finally {
      setImporting(false);
    }
  };

  if (!canAccess) {
    return (
      <Layout>
      <div className="p-8">
        <Alert variant="destructive">
          <AlertOctagon className="h-4 w-4" />
          <AlertTitle>Zugriff verweigert</AlertTitle>
          <AlertDescription>
            Sie benötigen Admin- oder Sekretariats-Rechte für den MSV-Import.
          </AlertDescription>
        </Alert>
      </div>
      </Layout>
    );
  }

  return (
    <Layout>
    <div className="p-8 space-y-6" data-testid="msv-import-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900">MSV Import</h1>
          <p className="text-slate-500 mt-1">
            Datenimport aus MSV-Quelldatenbank
          </p>
          <UrlFilterChip
            params={[{
              key: 'tab',
              label: 'Sprung',
              valueLabels: { conflicts: 'Konflikte' },
            }]}
          />
        </div>
        <Button onClick={loadStatus} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" />
          Status aktualisieren
        </Button>
      </div>

      {/* Mode Info */}
      <Alert className="border-blue-200 bg-blue-50">
        <Info className="h-4 w-4 text-blue-600" />
        <AlertTitle className="text-blue-800">Import-Verhalten</AlertTitle>
        <AlertDescription className="text-blue-700">
          <ul className="mt-2 ml-4 list-disc text-sm">
            <li><strong>Blockierend (Abbruch):</strong> Deaktivierte Lehrer, fehlende Lehrer</li>
            <li><strong>Nicht blockierend:</strong> Doppelte aktive Belegungen werden importiert und als <Badge variant="outline" className="ml-1 text-brand-red border-amber-300">Konflikt</Badge> markiert</li>
          </ul>
        </AlertDescription>
      </Alert>

      {/* Connection Status */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Database className="w-5 h-5" />
            Verbindungsstatus
          </CardTitle>
        </CardHeader>
        <CardContent>
          {status ? (
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                {status.connected ? (
                  <CheckCircle className="w-5 h-5 text-green-500" />
                ) : (
                  <XCircle className="w-5 h-5 text-red-500" />
                )}
                <span className={status.connected ? 'text-green-700' : 'text-red-700'}>
                  {status.connected ? 'Verbunden' : 'Nicht verbunden'}
                </span>
              </div>
              <div className="text-sm text-slate-500">
                <p>Pfad: <code className="bg-slate-100 px-1 rounded">{status.database_path}</code></p>
                {status.file_modified_at && (
                  <p className="mt-1 font-medium text-slate-700" data-testid="msv-file-date">
                    Datenstand vom {new Date(status.file_modified_at).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                    {status.file_size_mb ? ` · ${status.file_size_mb} MB` : ''}
                  </p>
                )}
                {status.tables && (
                  <p className="mt-1">Tabellen: {status.tables.join(', ') || 'Keine'}</p>
                )}
                {status.kb_record_count > 0 && (
                  <p className="mt-1">KB-Einträge: {status.kb_record_count}</p>
                )}
                {status.error && (
                  <p className="mt-1 text-red-600">Fehler: {status.error}</p>
                )}
              </div>
            </div>
          ) : (
            <p className="text-slate-500">Lade Status...</p>
          )}
        </CardContent>
      </Card>

      {/* Quelldatenbank aktualisieren (SQLite-Upload) */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-lg flex items-center gap-2">
            <Upload className="w-5 h-5" />
            Quelldatenbank aktualisieren
          </CardTitle>
          <CardDescription>
            Aktuelle MSV-Datei hochladen (.zip, .sqlite oder .db). Eine Zip wird ausgepackt. Die bisherige Datei wird als Backup gesichert.
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-3 flex-wrap">
            <input
              type="file"
              accept=".zip,.sqlite,.db,.sqlite3"
              onChange={(e) => setUploadFile(e.target.files?.[0] || null)}
              disabled={uploading}
              data-testid="msv-upload-file-input"
              className="text-sm text-slate-600 file:mr-3 file:py-2 file:px-4 file:rounded-lg file:border-0 file:bg-slate-900 file:text-white hover:file:bg-slate-800 file:cursor-pointer"
            />
            <Button
              onClick={uploadSourceDb}
              disabled={!uploadFile || uploading}
              data-testid="msv-upload-btn"
            >
              {uploading ? (
                <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
              ) : (
                <Upload className="w-4 h-4 mr-2" />
              )}
              {uploading ? `Hochladen... ${uploadProgress}%` : 'Hochladen & aktivieren'}
            </Button>
          </div>
          {uploading && (
            <div className="mt-3 w-full bg-slate-100 rounded-full h-2 overflow-hidden" data-testid="msv-upload-progress">
              <div className="bg-slate-900 h-2 rounded-full transition-all" style={{ width: `${uploadProgress}%` }} />
            </div>
          )}
          {uploadFile && !uploading && (
            <p className="mt-2 text-xs text-slate-500">
              Ausgewählt: {uploadFile.name} ({(uploadFile.size / (1024 * 1024)).toFixed(1)} MB)
            </p>
          )}
        </CardContent>
      </Card>

      {/* Preview Button */}
      <div className="flex gap-3">
        <Button 
          onClick={runPreview} 
          disabled={loading || !status?.connected}
          data-testid="preview-btn"
        >
          {loading ? (
            <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
          ) : (
            <FileText className="w-4 h-4 mr-2" />
          )}
          Vorschau laden
        </Button>
      </div>

      {/* Import Result Banner */}
      {importResult?.success && importResult.conflicts?.length > 0 && (
        <Alert className="border-amber-200 bg-brand-red/5">
          <AlertCircle className="h-4 w-4 text-brand-red" />
          <AlertTitle className="text-amber-800">
            Import abgeschlossen – {importResult.summary.conflicts} Konflikt(e) gefunden
          </AlertTitle>
          <AlertDescription className="text-amber-700">
            Alle Belegungen wurden importiert. Die Konflikte sind im System markiert und werden unten angezeigt.
          </AlertDescription>
        </Alert>
      )}

      {importResult?.success && importResult.conflicts?.length === 0 && (
        <Alert className="border-green-200 bg-green-50">
          <CheckCircle className="h-4 w-4 text-green-600" />
          <AlertTitle className="text-green-800">Import erfolgreich</AlertTitle>
          <AlertDescription className="text-green-700">
            {importResult.summary.imported} Belegungen importiert, {importResult.summary.ended ?? 0} beendet (Abgleich), {importResult.summary.prepaid ?? 0} Prepaid-Karten, {importResult.summary.trial_lessons ?? 0} Probestunden. Keine Konflikte.
          </AlertDescription>
        </Alert>
      )}

      {/* Conflicts Table After Import */}
      {importResult?.conflicts?.length > 0 && (
        <Card className="border-amber-200">
          <CardHeader className="pb-3 bg-amber-50 rounded-t-lg">
            <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
              <AlertTriangle className="w-5 h-5" />
              Doppelte aktive Belegungen ({importResult.conflicts.length})
            </CardTitle>
            <CardDescription className="text-brand-red">
              Diese Belegungen wurden importiert und als Konflikt markiert (has_conflict=true)
            </CardDescription>
          </CardHeader>
          <CardContent className="pt-4">
            <div className="overflow-x-auto">
              <table className="w-full text-sm" data-testid="conflicts-table">
                <thead>
                  <tr className="border-b bg-slate-50">
                    <th className="text-left py-2 px-3 font-medium">Kurs-Nr</th>
                    <th className="text-left py-2 px-3 font-medium">Person-Nr</th>
                    <th className="text-left py-2 px-3 font-medium">KB-Nummern</th>
                    <th className="text-left py-2 px-3 font-medium">Anzahl</th>
                    <th className="text-left py-2 px-3 font-medium">Konflikt-Key</th>
                  </tr>
                </thead>
                <tbody>
                  {importResult.conflicts.map((conflict, idx) => (
                    <tr key={`${conflict.course_nr}-${conflict.student_person_nr}-${idx}`} className="border-b hover:bg-slate-50">
                      <td className="py-2 px-3 font-mono">{conflict.course_nr}</td>
                      <td className="py-2 px-3 font-mono">{conflict.student_person_nr}</td>
                      <td className="py-2 px-3">
                        <code className="text-xs bg-slate-100 px-1 rounded">
                          {conflict.kb_nrs.join(', ')}
                        </code>
                      </td>
                      <td className="py-2 px-3">
                        <Badge variant="outline" className="text-brand-red border-amber-300">
                          {conflict.count}
                        </Badge>
                      </td>
                      <td className="py-2 px-3">
                        <code className="text-xs bg-amber-100 px-1 rounded text-amber-700">
                          {conflict.conflict_group_key}
                        </code>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Preview Results */}
      {preview && (
        <div className="space-y-4">
          {/* Summary Card */}
          <Card>
            <CardHeader className="pb-3">
              <CardTitle className="text-lg flex items-center gap-2">
                <Info className="w-5 h-5" />
                Vorschau-Ergebnis
              </CardTitle>
              <CardDescription>
                Referenzdatum: {preview.reference_date}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div className="p-3 bg-slate-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-slate-900">{preview.total_kb_records}</p>
                  <p className="text-sm text-slate-500">KB-Einträge gesamt</p>
                </div>
                <div className="p-3 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">{preview.active_kb_records}</p>
                  <p className="text-sm text-slate-500">Aktive Belegungen</p>
                </div>
                <div className="p-3 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">{preview.students_to_create}</p>
                  <p className="text-sm text-slate-500">Neue Schüler</p>
                </div>
                <div className="p-3 bg-brand-red/5 rounded-lg text-center">
                  <p className="text-2xl font-bold text-brand-red">{preview.courses_to_create}</p>
                  <p className="text-sm text-slate-500">Neue Kurse</p>
                </div>
                <div className="p-3 bg-amber-50 rounded-lg text-center" data-testid="preview-enrollments-to-end">
                  <p className="text-2xl font-bold text-brand-red">{preview.enrollments_to_end}</p>
                  <p className="text-sm text-slate-500">Zu beendende Belegungen</p>
                </div>
                <div className="p-3 bg-cyan-50 rounded-lg text-center" data-testid="preview-prepaid-active">
                  <p className="text-2xl font-bold text-cyan-600">{preview.prepaid_cards_active}</p>
                  <p className="text-sm text-slate-500">Aktive Prepaid-Karten</p>
                </div>
                <div className="p-3 bg-pink-50 rounded-lg text-center" data-testid="preview-trial-open">
                  <p className="text-2xl font-bold text-pink-600">{preview.trial_lessons_open}</p>
                  <p className="text-sm text-slate-500">Offene Probestunden</p>
                </div>
              </div>

              {/* Status Badge */}
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                {preview.can_proceed ? (
                  <Badge className="bg-green-100 text-green-800 hover:bg-green-100">
                    <CheckCircle className="w-3 h-3 mr-1" />
                    Import möglich
                  </Badge>
                ) : (
                  <Badge variant="destructive">
                    <XCircle className="w-3 h-3 mr-1" />
                    Import blockiert
                  </Badge>
                )}
                {preview.conflicts_detected?.length > 0 && (
                  <Badge variant="outline" className="text-brand-red border-amber-300">
                    <AlertTriangle className="w-3 h-3 mr-1" />
                    {preview.conflicts_detected.length} Duplikat(e) werden als Konflikt markiert
                  </Badge>
                )}
                {preview.blocking_errors?.length > 0 && (
                  <span className="text-sm text-red-600">
                    {preview.blocking_errors.length} Blocking Error(s)
                  </span>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Blocking Errors */}
          {preview.blocking_errors?.length > 0 && (
            <Card className="border-red-200">
              <CardHeader className="pb-3 bg-red-50 rounded-t-lg">
                <CardTitle className="text-lg flex items-center gap-2 text-red-800">
                  <AlertOctagon className="w-5 h-5" />
                  Blocking Errors ({preview.blocking_errors.length})
                </CardTitle>
                <CardDescription className="text-red-600">
                  Diese Fehler müssen im MSV-Quellsystem behoben werden
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4 space-y-3">
                {preview.blocking_errors.map((error, idx) => (
                  <Alert key={idx} variant="destructive">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertTitle className="text-sm font-medium">
                      {error.error_type === 'disabled_teacher' && 'Deaktivierter Lehrer'}
                      {error.error_type === 'missing_teacher' && 'Fehlender Lehrer'}
                    </AlertTitle>
                    <AlertDescription className="text-sm">
                      {error.message}
                    </AlertDescription>
                  </Alert>
                ))}
              </CardContent>
            </Card>
          )}

          {/* Conflicts Preview (Duplicates that will be imported) */}
          {preview.conflicts_detected?.length > 0 && (
            <Card className="border-amber-200">
              <CardHeader className="pb-3 bg-amber-50 rounded-t-lg">
                <CardTitle className="text-lg flex items-center gap-2 text-amber-800">
                  <AlertTriangle className="w-5 h-5" />
                  Erkannte Duplikate ({preview.conflicts_detected.length})
                </CardTitle>
                <CardDescription className="text-brand-red">
                  Diese werden importiert und als Konflikt markiert (has_conflict=true)
                </CardDescription>
              </CardHeader>
              <CardContent className="pt-4">
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="border-b bg-slate-50">
                        <th className="text-left py-2 px-3 font-medium">Kurs-Nr</th>
                        <th className="text-left py-2 px-3 font-medium">Person-Nr</th>
                        <th className="text-left py-2 px-3 font-medium">KB-Nummern</th>
                        <th className="text-left py-2 px-3 font-medium">Anzahl</th>
                      </tr>
                    </thead>
                    <tbody>
                      {preview.conflicts_detected.map((dup, idx) => (
                        <tr key={`${dup.course_nr}-${dup.student_person_nr}-${idx}`} className="border-b hover:bg-slate-50">
                          <td className="py-2 px-3 font-mono">{dup.course_nr}</td>
                          <td className="py-2 px-3 font-mono">{dup.student_person_nr}</td>
                          <td className="py-2 px-3">
                            <code className="text-xs bg-slate-100 px-1 rounded">
                              {dup.kb_nrs.join(', ')}
                            </code>
                          </td>
                          <td className="py-2 px-3">
                            <Badge variant="outline" className="text-brand-red border-amber-300">
                              {dup.count}
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Import Actions */}
          {preview.can_proceed && (
            <Card>
              <CardHeader className="pb-3">
                <CardTitle className="text-lg flex items-center gap-2">
                  <Upload className="w-5 h-5" />
                  Import ausführen
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex gap-3">
                  <Button 
                    onClick={() => runImport(true)} 
                    variant="outline"
                    disabled={importing}
                    data-testid="dry-run-btn"
                  >
                    {importing ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <FileText className="w-4 h-4 mr-2" />
                    )}
                    Dry-Run (Test)
                  </Button>
                  <Button 
                    onClick={() => runImport(false)} 
                    disabled={importing}
                    data-testid="import-btn"
                  >
                    {importing ? (
                      <RefreshCw className="w-4 h-4 mr-2 animate-spin" />
                    ) : (
                      <Upload className="w-4 h-4 mr-2" />
                    )}
                    Import starten
                  </Button>
                </div>
              </CardContent>
            </Card>
          )}
        </div>
      )}
    </div>
    </Layout>
  );
}
